// ── Canonical taste + viewer-archetype engine ────────────────────────────────
// ONE source of truth for "what kind of viewer are you" so Profile (TasteDNA),
// Stats, and Show DNA all show the SAME archetype instead of three different
// labels for the same person (audit 2026-06-29: "The Detective" vs "The
// Investigator" vs "The Prestige Drama Devotee"). Each page keeps its own
// visualization but feeds the archetype + genre breakdown from here.

export const GENRE_GROUPS = [
  { ids: [80, 9648],            label: 'Crime & Mystery', emoji: '🔍', color: '#a29bfe' },
  { ids: [18],                  label: 'Drama',           emoji: '🎭', color: '#74b9ff' },
  { ids: [35],                  label: 'Comedy',          emoji: '😂', color: '#ffeaa7' },
  { ids: [10759, 28],           label: 'Action',          emoji: '💥', color: '#ff7675' },
  { ids: [10765, 878],          label: 'Sci-Fi & Fantasy',emoji: '🚀', color: '#55efc4' },
  { ids: [99],                  label: 'Documentary',     emoji: '🎬', color: '#fdcb6e' },
  { ids: [10768, 36],           label: 'History & War',   emoji: '⚔️',  color: '#e17055' },
  { ids: [10766, 10764, 10767], label: 'Reality & Soap',  emoji: '🎤', color: '#fd79a8' },
  { ids: [16],                  label: 'Animation',       emoji: '🎨', color: '#6c5ce7' },
  { ids: [10751],               label: 'Family',          emoji: '👨‍👩‍👧', color: '#00cec9' },
]

// Ordered by priority — genre identity wins over watching behavior, so a
// drama-loving completionist reads as "The Prestige Watcher", not "The
// Completionist". The catch-all Eclectic is always last. (`test(genrePct,
// topNetworks, stats)` — stats = { avgEps, pctCompleted }.)
export const ARCHETYPES = [
  { label: 'The Detective',        emoji: '🔍', color: '#74b9ff', desc: 'You follow the clues, always.',            test: g => g['Crime & Mystery'] > 30 },
  { label: 'The Prestige Watcher', emoji: '🏆', color: '#f5c842', desc: 'Only the finest television.',              test: g => g['Drama'] > 35 },
  { label: 'The Sci-Fi Devotee',   emoji: '🚀', color: '#a29bfe', desc: 'The future is your genre.',                test: g => g['Sci-Fi & Fantasy'] > 25 },
  { label: 'The Action Junkie',    emoji: '💥', color: '#e17055', desc: 'Bigger, louder, faster.',                  test: g => g['Action'] > 25 },
  { label: 'The Curious Mind',     emoji: '🎓', color: '#2ecc71', desc: 'TV is how you learn.',                     test: g => g['Documentary'] > 20 },
  { label: 'The Comfort Viewer',   emoji: '😄', color: '#ffeaa7', desc: 'You watch to feel good.',                  test: g => (g['Comedy'] || 0) > 25 },
  { label: 'The Binge Machine',    emoji: '🍿', color: '#fd79a8', desc: "You don't stop until it's done.",          test: (_, __, t) => t.avgEps > 40 },
  { label: 'The Completionist',    emoji: '✅', color: '#00b894', desc: 'You finish what you start.',               test: (_, __, t) => t.pctCompleted > 60 },
  { label: 'The Explorer',         emoji: '🧭', color: '#55efc4', desc: 'You go wherever the story is good.',        test: (_, n) => (n?.length || 0) > 5 },
  { label: 'The Loyalist',         emoji: '🎯', color: '#fdcb6e', desc: "Long runs don't scare you.",               test: (_, __, t) => t.avgEps > 25 },
  { label: 'The Eclectic Viewer',  emoji: '🎬', color: '#b2bec3', desc: 'Impossible to pin down.',                  test: () => true },
]

// Archetype shown before there's enough data to read someone's taste.
export const NEWCOMER = { label: 'The Newcomer', emoji: '🌱', color: '#b2bec3', desc: 'Your story is just beginning.' }

export function computeTaste(items, ratings, statuses) {
  if (!items?.length) return null

  const genreCounts = {}, networkCounts = {}, decadeCounts = {}
  let totalShows = 0, completedShows = 0, totalEps = 0

  for (const show of items) {
    totalShows++
    if (statuses?.[show.id] === 'completed') completedShows++
    totalEps += show.number_of_episodes || 0

    for (const g of (show.genres || show.genre_ids || [])) {
      const gid = typeof g === 'object' ? g.id : g
      const group = GENRE_GROUPS.find(grp => grp.ids.includes(gid))
      if (group) genreCounts[group.label] = (genreCounts[group.label] || 0) + 1
    }
    // Primary network only — TMDB `networks` is an array; counting every entry tallied one
    // show under multiple networks, so the per-network share could exceed 100% (audit 2026-07-22).
    const primaryNet = (show.networks || [])[0]
    if (primaryNet?.name) networkCounts[primaryNet.name] = (networkCounts[primaryNet.name] || 0) + 1
    const year = parseInt(show.first_air_date?.slice(0, 4))
    if (year) {
      const decade = `${Math.floor(year / 10) * 10}s`
      decadeCounts[decade] = (decadeCounts[decade] || 0) + 1
    }
  }

  const genrePct = {}
  for (const [k, v] of Object.entries(genreCounts)) genrePct[k] = Math.round((v / totalShows) * 100)

  const topNetworks = Object.entries(networkCounts).sort((a, b) => b[1] - a[1]).slice(0, 5)
  const topDecades  = Object.entries(decadeCounts).sort((a, b) => b[1] - a[1]).slice(0, 3)
  const topGenres   = GENRE_GROUPS.filter(g => genrePct[g.label] > 0)
    .sort((a, b) => (genrePct[b.label] || 0) - (genrePct[a.label] || 0)).slice(0, 6)

  const stats = { avgEps: totalEps / totalShows, pctCompleted: (completedShows / totalShows) * 100 }
  const archetype = ARCHETYPES.find(a => a.test(genrePct, topNetworks, stats)) || ARCHETYPES[ARCHETYPES.length - 1]

  return { genrePct, topGenres, topNetworks, topDecades, archetype, stats, totalShows }
}

// Convenience for callers that only want the label/desc/emoji/color (with a
// minimum-data floor so brand-new users get The Newcomer rather than a guess).
export function archetypeOf(wl, minShows = 3) {
  const taste = computeTaste(wl?.items, wl?.ratings, wl?.statuses)
  if (!taste || taste.totalShows < minShows) return NEWCOMER
  return taste.archetype
}
